import { arc as d3Arc, pie as d3Pie } from 'd3-shape';

import { accentAt } from '../core/palette';

export interface PieDatum {
  label: string;
  value: number;
}

export interface PieSlicesProps {
  data: PieDatum[];
  /** Outer radius in px */
  radius: number;
  /** Donut hole as a fraction of the radius */
  innerRatio?: number;
  onHover?: (index: number | null, position: { x: number; y: number }) => void;
}

/** Computed pie geometry — pure so it can be asserted without a DOM. */
export function buildPieArcs(
  data: PieDatum[],
  radius: number,
  innerRatio: number
): { label: string; path: string; centroid: [number, number]; color: string }[] {
  const layout = d3Pie<PieDatum>()
    .value(datum => datum.value)
    .sort(null);
  const arcBuilder = d3Arc<{ startAngle: number; endAngle: number }>()
    .innerRadius(radius * innerRatio)
    .outerRadius(radius);

  return layout(data).map((slice, index) => ({
    label: slice.data.label,
    path: arcBuilder(slice) ?? '',
    centroid: arcBuilder.centroid(slice),
    color: accentAt(index)
  }));
}

/** Donut/pie slices centred on the origin of the enclosing group. */
export const PieSlices = ({ data, radius, innerRatio = 0.45, onHover }: PieSlicesProps) => {
  const arcs = buildPieArcs(data, radius, innerRatio);

  return (
    <g data-slot='chart-pie'>
      {arcs.map((slice, index) => (
        <path
          key={`${index}-${slice.label}`}
          data-slot='chart-slice'
          d={slice.path}
          fill={slice.color}
          onPointerEnter={() => onHover?.(index, { x: slice.centroid[0], y: slice.centroid[1] })}
          onPointerLeave={() => onHover?.(null, { x: 0, y: 0 })}
        />
      ))}
    </g>
  );
};

PieSlices.displayName = 'PieSlices';
